import { AllFTUXes, ftuxMaxDictionary, ftuxMinDictionary } from '@aloft/toolkit-shared';

import { get } from 'lodash';
import { useCallback, useRef } from 'react';

import { useGetAccount, useUpdateAccount } from '../../hooks/api/toolkitComponents.ts';
import { bulkSetFTUXCounts } from '../../redux/features/accountSlice.ts';
import { selectFTUXes } from '../../redux/features/selectors/accountSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { FTUXInstance } from '../types.ts';

let updates: Partial<AllFTUXes>[] = [];

const initializeCount = (valFromStorage: number, max: number): number => {
  if (Number.isNaN(valFromStorage)) {
    // migration issues; previously stored values may not work correctly.
    // let's assume if you have a previously stored value, you don't need
    // to see this FTUX again, and we'll set you over-max.
    return max + 1;
  }

  return valFromStorage;
};

const useUpdateFtux = () => {
  const dispatch = useAppDispatch();
  const ftuxes = useAppSelector(selectFTUXes);
  const getAccount = useGetAccount({});
  const { mutateAsync: updateAccount } = useUpdateAccount({});

  return useCallback(
    (updateKey: keyof AllFTUXes, count: number) => {
      updates.push({ [updateKey]: count });

      setTimeout(() => {
        if (updates.length === 0) return;

        const updatedFTUXDictionary: AllFTUXes = updates.reduce(
          (acc, update) => ({
            ...acc,
            ...update,
          }),
          ftuxes,
        ) as AllFTUXes;
        dispatch(bulkSetFTUXCounts(updates));
        updateAccount({
          body: {
            ...getAccount.data,
            body: {
              ...getAccount.data?.body,
              ftux: updatedFTUXDictionary,
            },
          },
        });
        updates = [];
      }, 100);
    },
    [dispatch, ftuxes, getAccount.data, updateAccount],
  );
};

export const useFTUX = (key: keyof AllFTUXes): FTUXInstance => {
  const hasIncrementedFTUXCountRef = useRef<Partial<Record<keyof AllFTUXes, boolean>>>({});
  const ftuxes = useAppSelector(selectFTUXes);

  const min = ftuxMinDictionary[key];
  const max = ftuxMaxDictionary[key];
  const currentFTUXCount = get(ftuxes, key);
  const ftuxCountRef = useRef<number>(initializeCount(currentFTUXCount, max));

  const updateFTUX = useUpdateFtux();

  const ftux = () => {
    const hasNotIncrementedDuringThisView = !hasIncrementedFTUXCountRef.current[key];
    const isNotAlreadyOverMax = ftuxCountRef.current <= max;

    if (hasNotIncrementedDuringThisView && isNotAlreadyOverMax) {
      hasIncrementedFTUXCountRef.current[key] = true;
      ftuxCountRef.current += 1;
      updateFTUX(key, ftuxCountRef.current);
    }

    return ftuxCountRef.current >= min && ftuxCountRef.current <= max;
  };

  const dismiss = () => {
    ftuxCountRef.current = max + 1;
    updateFTUX(key, ftuxCountRef.current);
  };

  return { ftux, dismiss };
};

// helper/dev functions; shouldn't ship with these in place
// eslint-disable-next-line @typescript-eslint/no-unused-vars, camelcase
const useDangerDevOnly__ClearFtux = (key: keyof AllFTUXes) => {
  const calledRef = useRef<boolean>(false);
  const updateFTUX = useUpdateFtux();

  if (!calledRef.current) {
    calledRef.current = true;
    updateFTUX(key, 0);
  }
};
